import { format, isSameDay, startOfDay, subDays } from 'date-fns';
import type { AdvancedDateRange, Preset } from './DateRangePicker.types';

export const PRESET_LABELS: Record<Preset, string> = {
  today: 'Today',
  last7: 'Last 7 days',
  last30: 'Last 30 days',
  last60: 'Last 60 days',
  last90: 'Last 90 days',
  allTime: 'All time',
  custom: 'Custom',
};

const PRESET_OFFSETS: Record<Exclude<Preset, 'custom' | 'allTime'>, number> = {
  today: 0,
  last7: 7,
  last30: 30,
  last60: 60,
  last90: 90,
};

export function getPresetRange(preset: Preset, reference: Date = new Date()): AdvancedDateRange {
  if (preset === 'custom') {
    return [null, null];
  }
  if (preset === 'allTime') {
    return [null, null, 0, 'allTime'];
  }
  const offset = PRESET_OFFSETS[preset];
  const end = reference;
  const start = offset === 0 ? startOfDay(reference) : startOfDay(subDays(reference, offset));
  return [start, end, offset, 'day'];
}

export function detectPreset(value: AdvancedDateRange, presets: Preset[]): Preset | null {
  const [start, end, offset, unit] = value as [Date | null, Date | null, number?, string?];
  if (unit === 'allTime' && presets.includes('allTime')) return 'allTime';

  // Offset-based presets currently assume `unit === 'day'`; extend PRESET_OFFSETS
  // and getPresetRange if other units (week, month, etc.) are ever introduced.
  if (offset !== undefined && unit === 'day') {
    const byOffset = (Object.keys(PRESET_OFFSETS) as Array<keyof typeof PRESET_OFFSETS>).find(
      (p) => PRESET_OFFSETS[p] === offset,
    );
    if (byOffset && presets.includes(byOffset)) return byOffset;
  }

  // Fallback: match by exact start/end dates against known presets.
  // Enables highlighting when callers pass just [start, end] without [offset, unit]
  // metadata (e.g. after reconstructing from persisted epoch filters).
  if (start && end) {
    const today = new Date();
    for (const preset of Object.keys(PRESET_OFFSETS) as Array<keyof typeof PRESET_OFFSETS>) {
      if (!presets.includes(preset)) continue;
      const [pStart, pEnd] = getPresetRange(preset, today) as [Date, Date, number, string];
      if (isSameDay(start, pStart) && isSameDay(end, pEnd)) return preset;
    }
  }

  return null;
}

const MONTH_DAY_YEAR = 'MMM d, yyyy';

export function formatRangeLabel(value: AdvancedDateRange, mode: 'single' | 'range', presets: Preset[]): string {
  const [start, end, , unit] = value as [Date | null, Date | null, number?, string?];
  if (unit === 'allTime') return PRESET_LABELS.allTime;

  const preset = detectPreset(value, presets);
  if (preset && preset !== 'custom') return PRESET_LABELS[preset];

  if (mode === 'single') {
    if (!start) return 'Select a date';
    return format(start, MONTH_DAY_YEAR);
  }

  if (!start || !end) return 'Select a date range';
  if (isSameDay(start, end)) return format(start, MONTH_DAY_YEAR);
  return `${format(start, MONTH_DAY_YEAR)} - ${format(end, MONTH_DAY_YEAR)}`;
}

export function tupleToRange(value: AdvancedDateRange): { from: Date | undefined; to: Date | undefined } {
  const [start, end] = value as [Date | null, Date | null];
  return {
    from: start ?? undefined,
    to: end ?? undefined,
  };
}

export function rangeToTuple(from: Date | undefined, to: Date | undefined): AdvancedDateRange {
  return [from ?? null, to ?? null];
}
